/**
 * Admin Layout
 * Layout for admin pages with sidebar navigation
 */

import { Outlet, Navigate, NavLink, useLocation } from 'react-router-dom';
import { useAuthStore } from '@schoolidb/store';
import { ToastContainer } from '../components/ToastContainer';

const adminNavItems = [
  { path: '/admin', label: 'Dashboard', icon: '📊' },
  { path: '/admin/claims', label: 'Claims', icon: '📋' },
  { path: '/admin/stats', label: 'Statistics', icon: '📈' },
  { path: '/admin/school-ops', label: 'School Operations', icon: '🔍' },
  { path: '/admin/crawl-status', label: 'Crawl Status', icon: '🕷️' },
  { path: '/admin/rank-status', label: 'Rank Status', icon: '⭐' },
  { path: '/admin/users', label: 'Users', icon: '👥' },
  { path: '/admin/ai', label: 'AI Features', icon: '🤖' },
];

export function AdminLayout() {
  const location = useLocation();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated());
  const user = useAuthStore((state) => state.user);

  // Check if user is admin (case-insensitive check)
  const isAdmin = user?.role?.toLowerCase() === 'admin';

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen flex bg-gray-100 dark:bg-gray-900">
      {/* Sidebar */}
      <aside className="w-64 bg-white dark:bg-gray-800 border-r dark:border-gray-700 flex flex-col">
        <div className="p-6 border-b dark:border-gray-700">
          <h1 className="text-xl font-bold text-primary-600">Admin Panel</h1>
        </div>
        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {adminNavItems.map((item) => (
              <li key={item.path}>
                <NavLink
                  to={item.path}
                  end={item.path === '/admin'}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                      isActive
                        ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400'
                        : 'text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700'
                    }`
                  }
                >
                  <span>{item.icon}</span>
                  <span>{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <div className="p-4 border-t dark:border-gray-700">
          <NavLink
            to="/"
            className="flex items-center gap-3 px-4 py-3 text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 rounded-lg"
          >
            <span>←</span>
            <span>Back to Site</span>
          </NavLink>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col">
        <header className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 px-8 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              School iDB Administration
            </h2>
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {user?.email}
              </span>
            </div>
          </div>
        </header>
        <main className="flex-1 p-8 overflow-auto">
          <Outlet />
        </main>
      </div>
      <ToastContainer />
    </div>
  );
}
